import React, { useMemo, useState, useRef, useEffect } from 'react';
import { RefreshCw, Calendar, Loader2 } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'slices';
import { fetchSalesReport, clearError } from 'slices/salesReport/salesReportSlice';
import { getRoleFromAccessToken } from 'helpers/jwt';
import { getRoleBasedUrl } from 'Routes/allRoutes';

type Row = {
  id: number;
  game: string;
  time: string;
  totalBet: number;
  distributed: number;
};

const SalesReport: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { data: salesData, summary, loading, error } = useSelector((state: RootState) => state.SalesReport);
  const { user } = useSelector((state: RootState) => state.Login);
  const role =
    ((user as any)?.role as string | undefined) ||
    getRoleFromAccessToken();
  const location = useLocation();
  
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<keyof Row>('id');
  const [asc, setAsc] = useState(false);
  const navigate = useNavigate();
  
  // Get selected date from location state if navigating back from detail page, otherwise use current date
  const getInitialDate = () => {
    const state = location.state as { selectedDate?: string } | null;
    if (state?.selectedDate) {
      return state.selectedDate;
    }
    return new Date().toISOString().slice(0, 10);
  };
  
  const [selectedDate, setSelectedDate] = useState<string>(getInitialDate);
  const dateInputRef = useRef<HTMLInputElement>(null);
  const processedStateRef = useRef<string | null>(null);
  const lastFetchedDateRef = useRef<string | null>(null);

  // Update selectedDate when navigating back from detail page
  useEffect(() => {
    const state = location.state as { selectedDate?: string } | null;
    if (state?.selectedDate) {
      const stateDate = state.selectedDate;
      // Only update if we haven't processed this state yet and it's different from current
      if (processedStateRef.current !== stateDate && stateDate !== selectedDate) {
        setSelectedDate(stateDate);
        processedStateRef.current = stateDate;
      }
    }
  }, [location.state, selectedDate]);

  // Fetch sales report data on mount and when date changes (only if date actually changed)
  useEffect(() => {
    // Only fetch if date has actually changed
    if (lastFetchedDateRef.current !== selectedDate) {
      lastFetchedDateRef.current = selectedDate;
      dispatch(fetchSalesReport({
        date: selectedDate // Use single date parameter
      }));
    }
  }, [dispatch, selectedDate]);

  // Clear error when component unmounts
  useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  const toggleSort = (key: keyof Row) => {
    if (sortKey === key) setAsc(!asc); else { setSortKey(key); setAsc(true); }
  };

  // Map API data to Row format with original item reference
  const rows = useMemo(() => {
    return salesData.map((item) => ({
      id: item.sl_no,
      game: item.game_display,
      time: item.time,
      totalBet: item.total_bets,
      distributed: parseFloat(item.distributed_amount) || 0,
      // Include original item data for Access button
      market_id: item.market_id,
      session: item.session,
      date: item.date,
    }));
  }, [salesData]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    const list = rows.filter(r => !q || r.game.toLowerCase().includes(q) || r.time.includes(q));
    list.sort((a,b) => {
      const va = a[sortKey]; const vb = b[sortKey];
      const res = typeof va === 'number' && typeof vb === 'number' ? va - vb : String(va).localeCompare(String(vb));
      return asc ? res : -res;
    });
    return list;
  }, [rows, search, sortKey, asc]);

  const formatDateOnly = (dateString: string) => {
    const d = new Date(dateString);
    return d.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Get today's date in YYYY-MM-DD format for max date
  const getTodayDate = () => {
    return new Date().toISOString().slice(0, 10);
  };

  // const handleRefresh = () => {
  //   // Placeholder for future API fetch; mirrors Markets page UX
  //   setSearch('');
  //   setRows(getDummyRows());
  // };

  return (
    <div className="p-6">
      <div className="bg-white rounded-xl shadow-sm border mb-6">
        <div className="p-6 border-b flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">Sales Report</h1>
          <div className="flex gap-2 items-center">
            <div className="relative">
              <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
              <input
                value={search}
                onChange={(e)=>setSearch(e.target.value)}
                placeholder="Search for ..."
                className="pl-10 pr-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full sm:w-80 bg-gray-50 focus:bg-white transition-all duration-200"
              />
            </div>
            <div className="text-sm text-gray-500 relative overflow-visible">
              <button
                type="button"
                onClick={() => dateInputRef.current?.showPicker?.() || dateInputRef.current?.focus()}
                className="inline-flex items-center gap-1 text-gray-600 hover:text-gray-900"
                title="Pick a date"
              >
                <Calendar className="w-4 h-4" />
                <span>{selectedDate ? formatDateOnly(selectedDate) : formatDateOnly(new Date().toISOString())}</span>
              </button>
              {/* hidden native date input to open OS calendar */}
              <input
                ref={dateInputRef}
                type="date"
                value={selectedDate}
                max={getTodayDate()}
                onChange={(e) => {
                  const v = e.target.value;
                  // Prevent selecting future dates
                  if (v <= getTodayDate()) {
                    setSelectedDate(v);
                  }
                  // API call is handled by useEffect when selectedDate changes
                }}
                className="absolute right-0 top-0 w-8 h-8 opacity-0"
                aria-hidden="true"
              />
            </div>
            {/* <button
              onClick={handleRefresh}
              className="flex items-center gap-2 bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 text-white px-6 py-2 rounded-xl font-medium transition-all duration-200 shadow-md hover:shadow-lg"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button> */}
          </div>
        </div>

        <div className="p-6 overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600 mr-2" />
              <span className="text-gray-600">Loading sales report data...</span>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-w-md w-full">
                <p className="text-red-800 font-medium mb-2">Error loading sales report</p>
                <p className="text-red-600 text-sm">{error}</p>
                <button
                  onClick={() => dispatch(fetchSalesReport({
                    date: selectedDate
                  }))}
                  className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
                >
                  Retry
                </button>
              </div>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr className="text-xs font-medium text-gray-600 uppercase tracking-wider">
                  <th className="px-4 py-3 cursor-pointer" onClick={()=>toggleSort('id')}>SL. No</th>
                  <th className="px-4 py-3 cursor-pointer text-left" onClick={()=>toggleSort('game')}>Game</th>
                  <th className="px-4 py-3 cursor-pointer" onClick={()=>toggleSort('time')}>Time</th>
                  <th className="px-4 py-3 cursor-pointer" onClick={()=>toggleSort('totalBet')}>Total Bet</th>
                  <th className="px-4 py-3 cursor-pointer" onClick={()=>toggleSort('distributed')}>Distributed Amount</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filtered.map((r, i)=> (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-center text-sm text-gray-700">{r.id}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{r.game}</td>
                    <td className="px-4 py-3 text-center text-sm text-gray-700">{r.time}</td>
                    <td className="px-4 py-3 text-center text-sm font-semibold text-gray-900">{r.totalBet}</td>
                    <td className="px-4 py-3 text-center text-sm text-gray-700">{r.distributed.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={()=> navigate(getRoleBasedUrl('/sales-report/data', role), { 
                          state: { 
                            market_id: r.market_id,
                            session: r.session,
                            date: r.date,
                            game_display: r.game,
                            selectedDate: selectedDate // Pass current selected date to preserve it when navigating back
                          } 
                        })}
                        className="px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-xs"
                      >
                        Access
                      </button>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && !loading && (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-gray-500 text-sm">No data available</td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* row details moved to SalesData page */}
    </div>
  );
};

export default SalesReport;


